import { apiClient as api } from "./httpClient";
import { DocumentTemplateVariable } from "./patientDocumentsService";

/**
 * Modelos de contrato versionados (Prontuário e documentos → Modelos de contrato). Rascunho edita à
 * vontade; Publicar congela a versão; mudança vira nova versão (cópia da última) com nota do que mudou.
 * A emissão ao paciente (seção Contratos da aba Documentos) é a fase seguinte e terá serviço próprio.
 */
export type ContractVersionStatus = "draft" | "published" | "archived";

export type ContractFieldType = "text" | "long_text" | "date" | "number" | "money" | "option";

export const CONTRACT_FIELD_TYPE_LABELS: Record<ContractFieldType, string> = {
  text: "Texto curto",
  long_text: "Texto longo",
  date: "Data",
  number: "Número",
  money: "Valor em R$",
  option: "Opções",
};

export const CONTRACT_STATUS_LABELS: Record<ContractVersionStatus, string> = {
  draft: "Rascunho",
  published: "Publicada",
  archived: "Arquivada",
};

export interface ContractCustomField {
  /** Identificador no texto: {campo.<key>} */
  key: string;
  label: string;
  type: ContractFieldType;
  required: boolean;
  help: string | null;
  options: string[] | null;
}

export type ContractTemplateAudience = "patient" | "professional";

export const CONTRACT_TEMPLATE_AUDIENCE_LABELS: Record<ContractTemplateAudience, string> = {
  patient: "Paciente",
  professional: "Profissional",
};

export interface ContractTemplateVersionRow {
  id: string;
  version: number;
  status: ContractVersionStatus;
  requiresSignature: boolean;
  /** cláusula de renovação automática no texto: vencida a vigência, o contrato segue vigente até cancelar */
  autoRenew: boolean;
  /** yyyy-mm-dd */
  validFrom: string | null;
  changeNote: string | null;
  createdByName: string | null;
  /** ISO UTC */
  createdDate: string;
  publishedAt: string | null;
  publishedByName: string | null;
}

export interface ContractTemplateRow {
  id: string;
  title: string;
  description: string | null;
  /** a quem o contrato se destina: decide as variáveis e em que tela o modelo é emitido */
  audience: ContractTemplateAudience;
  active: boolean;
  createdByName: string | null;
  createdDate: string;
  /** Contratos emitidos a partir de qualquer versão: com um que seja, o modelo só inativa */
  contractsCount: number;
  /** Da mais recente para a mais antiga */
  versions: ContractTemplateVersionRow[];
}

export interface ContractTemplateVersionDetail extends ContractTemplateVersionRow {
  templateId: string;
  title: string;
  description: string | null;
  audience: ContractTemplateAudience;
  templateActive: boolean;
  content: string;
  customFields: ContractCustomField[];
  printTemplateId: string | null;
  versions: ContractTemplateVersionRow[];
}

export interface SaveContractTemplatePayload {
  id?: string | null;
  title: string;
  description: string | null;
  /** só na criação (ou enquanto nenhum contrato foi emitido); omitido = mantém */
  audience?: ContractTemplateAudience;
  active: boolean;
}

export interface SaveContractTemplateVersionPayload {
  content: string | null;
  customFields: ContractCustomField[];
  printTemplateId: string | null;
  requiresSignature: boolean;
  autoRenew: boolean;
  validFrom: string | null;
  changeNote: string | null;
}

const ERRORS: Record<string, string> = {
  "ContractTemplates.TitleRequired": "Informe o título do modelo.",
  "ContractTemplates.TitleTooLong": "O título é longo demais.",
  "ContractTemplates.TitleDuplicated": "Já existe um modelo com este título.",
  "ContractTemplates.AudienceInvalid": "Informe o destinatário do modelo: paciente ou profissional.",
  "ContractTemplates.TemplateInUseReadOnly": "Modelo com contrato emitido não muda de título, descrição nem destinatário; só pode ser ativado ou inativado.",
  "ContractTemplates.ContentRequired": "Escreva o texto do contrato antes de publicar.",
  "ContractTemplates.ContentTooLong": "O texto do contrato é longo demais.",
  "ContractTemplates.VersionNotDraft": "Esta versão já foi publicada e não pode ser alterada. Crie uma nova versão.",
  "ContractTemplates.DraftAlreadyExists": "O modelo já tem um rascunho. Publique ou exclua o rascunho antes de criar outra versão.",
  "ContractTemplates.TemplateInactive": "O modelo está inativo. Ative-o para publicar.",
  "ContractTemplates.TemplateHasPublishedVersions": "Modelo com versão publicada não pode ser excluído, só inativado.",
  "ContractTemplates.TemplateInUse": "Modelo com contrato emitido não pode ser excluído, só inativado.",
  "ContractTemplates.TooManyFields": "O contrato tem campos personalizados demais.",
  "ContractTemplates.InvalidFieldKey": "A chave de um campo personalizado é inválida: use letras minúsculas, dígitos e sublinhado, começando por letra.",
  "ContractTemplates.DuplicatedFieldKey": "Há dois campos personalizados com a mesma chave.",
  "ContractTemplates.FieldLabelRequired": "Todo campo personalizado precisa de um rótulo.",
  "ContractTemplates.FieldOptionsRequired": "Campo de opções precisa de pelo menos duas opções.",
  "ContractTemplates.UndefinedFieldInContent": "O texto usa um campo personalizado que não está definido.",
};

export function contractErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { code?: string; description?: string } } })?.response?.data;
  if (data?.code && ERRORS[data.code]) return ERRORS[data.code];
  // Mensagens do servidor já vêm em português para as regras dos campos (trazem a chave em questão)
  if (data?.code?.startsWith("ContractTemplates.") && data.description) return data.description;
  return fallback;
}

export const contractTemplatesService = {
  async getAll(): Promise<ContractTemplateRow[]> {
    const response = await api.get<ContractTemplateRow[]>("/contract-templates");
    return response.data;
  },

  async getVariables(audience: ContractTemplateAudience = "patient"): Promise<DocumentTemplateVariable[]> {
    const response = await api.get<DocumentTemplateVariable[]>("/contract-templates/variables", { params: { audience } });
    return response.data;
  },

  async save(payload: SaveContractTemplatePayload): Promise<{ templateId: string; draftVersionId: string | null }> {
    const response = await api.post<{ templateId: string; draftVersionId: string | null }>("/contract-templates", payload);
    return response.data;
  },

  async remove(templateId: string): Promise<void> {
    await api.delete(`/contract-templates/${templateId}`);
  },

  /** Modelo novo e independente, v1 em rascunho copiada da publicada (ou da mais recente). */
  async duplicate(templateId: string, title: string): Promise<{ templateId: string; draftVersionId: string | null }> {
    const response = await api.post<{ templateId: string; draftVersionId: string | null }>(`/contract-templates/${templateId}/duplicate`, { title });
    return response.data;
  },

  async createVersion(templateId: string): Promise<string> {
    const response = await api.post<{ id: string }>(`/contract-templates/${templateId}/versions`);
    return response.data.id;
  },

  async getVersion(versionId: string): Promise<ContractTemplateVersionDetail> {
    const response = await api.get<ContractTemplateVersionDetail>(`/contract-templates/versions/${versionId}`);
    return response.data;
  },

  async saveVersion(versionId: string, payload: SaveContractTemplateVersionPayload): Promise<void> {
    await api.put(`/contract-templates/versions/${versionId}`, payload);
  },

  async publish(versionId: string): Promise<void> {
    await api.post(`/contract-templates/versions/${versionId}/publish`);
  },

  async removeVersion(versionId: string): Promise<void> {
    await api.delete(`/contract-templates/versions/${versionId}`);
  },

  /** Prévia com exemplos nas variáveis e o rótulo no lugar de cada campo personalizado. */
  async preview(content: string, customFields: ContractCustomField[]): Promise<string> {
    const response = await api.post<{ content: string }>("/contract-templates/preview", { content, customFields });
    return response.data.content;
  },
};
